import type { NotificationReportCreateRequest } from 'c-sdk';
import type { NotificationReport } from 'c-entity-types';
import * as yup from 'yup';

// The create form and list cells of the scheduled notification reports page.

export type CreateNotificationFormValues = {
    name: string;
    interval: string;
    email_subject: string;
    email_body: string;
    email_addresses: string;
};

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const createSchema = yup.object({
    name: yup.string().required(),
    interval: yup.string().required(),
    email_subject: yup.string().nullable(),
    email_body: yup.string().nullable(),
    email_addresses: yup.string().nullable(),
});

const recipientLines = (value: unknown): string[] => {
    const raw = typeof value === 'string' ? value : value != null ? String(value) : '';
    return raw
        .split('\n')
        .map(entry => entry.trim())
        .filter(entry => entry.length > 0);
};

// How many of the typed recipients (one per line) are valid addresses: the "N recipients"
// helper under the field.
export const getValidEmailCount = (value: string | string[] | null | undefined): number => {
    const raw = typeof value === 'string' ? value : value != null ? String(value) : '';
    if (!raw) return 0;
    return recipientLines(raw).filter(entry => emailRegex.test(entry)).length;
};

// The body of POST report. Invalid addresses are dropped; blank optional fields become null.
export const transformCreateValues = (values: CreateNotificationFormValues) => {
    const emailAddresses = recipientLines(values.email_addresses).filter(entry =>
        emailRegex.test(entry),
    );

    const payload: NotificationReportCreateRequest = {
        name: values.name?.trim() ?? '',
        interval: values.interval?.trim() ?? '',
        email_subject: values.email_subject?.trim() || null,
        email_body: values.email_body?.trim() || null,
        email_addresses: emailAddresses.length > 0 ? emailAddresses : null,
    };

    return payload as Partial<NotificationReport>;
};

export const formatDate = (value: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
};

export const formatRecipients = (emailAddresses: string[] | null) => {
    if (!emailAddresses) return 0;
    return emailAddresses.length;
};
